import { Button, Link, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { valueByName } from './form_utils';
import { MeetupContext } from '@/MeetupContext';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import FormLabel from '@mui/material/FormLabel';
import clsx from 'clsx';

const PREFIX = 'ECFileField';

const classes = {
  fieldError: `${PREFIX}-fieldError`,
  redError: `${PREFIX}-redError`,
};

const Root = styled('div')(() => ({
  marginTop: '8px',
  marginBottom: '4px',

  [`& .${classes.fieldError}`]: {
    marginLeft: 14,
    marginTop: 4,
    fontSize: 11,
    display: 'block',
  },

  [`& .${classes.redError}`]: {
    color: 'red',
  },
}));

// Загрузка произвольного файла (по умолчанию PDF) через выделенный эндпоинт /api/file
// (отдельный от картинок: без image-валидации, своя таблица files). В форму кладём fileId,
// а реальное имя файла отдаём наружу через onUploaded, чтобы родитель проставил filename.
export const ECFileField = props => {
  const { name, label, form, initial, accept = 'application/pdf', onUploaded } = props;

  const meetupContext = useContext(MeetupContext);

  const [value, setValue] = useState(valueByName(initial, name));

  useEffect(() => {
    form.register(name);
    form.setValue(name, value);
  }, [form.register]);

  const uploadFile = event => {
    const file = event.target.files[0];
    if (!file) return;
    const params = new URLSearchParams();
    if (meetupContext && meetupContext.meetupId) params.set('mid', meetupContext.meetupId);
    if (file.name) params.set('filename', file.name);
    const formData = new FormData();
    formData.append('file', file);

    // Авторизация как у остальных API-вызовов: Bearer-токен сессии из localStorage
    // (Content-Type не выставляем — браузер сам проставит multipart с boundary).
    const sessionId = localStorage.getItem('sessionId');
    const headers = sessionId ? { Authorization: 'Bearer ' + sessionId } : {};

    fetch('/api/file?' + params.toString(), { method: 'POST', headers, body: formData })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw data;
        form.setValue(name, data.fileId);
        form.clearErrors(name);
        setValue(data.fileId);
        if (onUploaded) onUploaded(file);
      })
      .catch(err => {
        form.setError(name, err.error || 'error', err.message || 'Не удалось загрузить файл');
      });
  };

  const deleteFile = () => {
    form.setValue(name, null);
    form.clearErrors(name);
    setValue(null);
  };

  const error = form.formState.errors[name];

  return (
    <Root>
      <input
        accept={accept}
        style={{ display: 'none' }}
        id={'input-file-' + name}
        type='file'
        onChange={uploadFile}
      />
      <Stack direction='row' spacing={1} alignItems='center'>
        <label htmlFor={'input-file-' + name}>
          <Button component='span' variant='outlined' size='small' startIcon={<CloudDownloadIcon />}>
            {value ? 'Заменить файл' : label}
          </Button>
        </label>
        {value && (
          <>
            <Link href={'/api/file/' + value} target='_blank' rel='noopener'>
              открыть
            </Link>
            <CloseIcon style={{ cursor: 'pointer' }} onClick={deleteFile} />
          </>
        )}
      </Stack>
      {error && <FormLabel className={clsx(classes.fieldError, classes.redError)}>{error.message}</FormLabel>}
    </Root>
  );
};

ECFileField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  form: PropTypes.object,
  initial: PropTypes.object,
  accept: PropTypes.string,
  onUploaded: PropTypes.func,
};
